import React, { useState, useEffect } from 'react';
import './CommitteeMembers.css';
import API_BASE_URL from '../config/api';

const SECTIONS = {
  MAR_THOMA: "Mar Thoma Churches",
  MISSION_FIELD: "Mission Field", 
  OTHER_DENOMINATION: "Other Denomination Churches"
};

const SECTION_ORDER = ["MAR_THOMA", "MISSION_FIELD", "OTHER_DENOMINATION"];

const ChurchesAroundUs = () => {
  const [churches, setChurches] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [addingSection, setAddingSection] = useState(null);
  const [newChurchData, setNewChurchData] = useState({
    name: '',
    address: '',
    mapsLocation: '',
    sortPriority: '',
    vicars: []
  });
  const [expandedVicars, setExpandedVicars] = useState(new Set());

  const handleAddClick = (section) => {
    setAddingSection(section);
    setNewChurchData({
      name: '',
      address: '',
      mapsLocation: '',
      sortPriority: '',
      vicars: []
    });
  };

  const handleCancelAdd = () => {
    setAddingSection(null);
    setNewChurchData({
      name: '',
      address: '',
      mapsLocation: '',
      sortPriority: '',
      vicars: []
    });
  };

  const handleNewChurchChange = (e) => {
    const { name, value } = e.target;
    setNewChurchData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddNewChurch = async () => {
    if (!newChurchData.name || !newChurchData.address) {
      setError("Name and Address are required.");
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/churches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          ...newChurchData,
          section: addingSection,
          sortPriority: parseInt(newChurchData.sortPriority) || 1
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add church');
      }

      handleCancelAdd();
      fetchChurches();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditClick = (church) => {
    setEditingId(church.id);
    setEditingData({ ...church });
  };

  const handleCancelClick = () => {
    setEditingId(null);
    setEditingData(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveClick = async () => {
    if (!editingData) return;

    // Find the section name for the church being edited
    let sectionName = '';
    for (const section of SECTION_ORDER) {
      if (churches[section]?.some(church => church.id === editingId)) {
        sectionName = section;
        break;
      }
    }

    if (!sectionName) {
      setError("Could not determine the church's section. Please try again.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/churches/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          ...editingData,
          section: sectionName,
          sortPriority: parseInt(editingData.sortPriority) || 1
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save church');
      }

      setEditingId(null);
      setEditingData(null);
      fetchChurches();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteClick = async (churchId) => {
    if (window.confirm('Are you sure you want to delete this church?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/churches/${churchId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to delete church');
        }

        fetchChurches();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const toggleVicarsExpanded = (churchId) => {
    const newExpanded = new Set(expandedVicars);
    if (newExpanded.has(churchId)) {
      newExpanded.delete(churchId);
    } else {
      newExpanded.add(churchId);
    }
    setExpandedVicars(newExpanded);
  };

  const handleVicarChange = (vicarIndex, field, value) => {
    if (editingId) {
      const newVicars = [...editingData.vicars];
      newVicars[vicarIndex] = { ...newVicars[vicarIndex], [field]: value };
      setEditingData(prev => ({ ...prev, vicars: newVicars }));
    } else {
      const newVicars = [...newChurchData.vicars];
      newVicars[vicarIndex] = { ...newVicars[vicarIndex], [field]: value };
      setNewChurchData(prev => ({ ...prev, vicars: newVicars }));
    }
  };

  const addVicar = () => {
    const newVicar = { title: '', name: '', mobile: '', email: '' };
    if (editingId) {
      setEditingData(prev => ({ 
        ...prev, 
        vicars: [...(prev.vicars || []), newVicar] 
      }));
    } else {
      setNewChurchData(prev => ({ 
        ...prev, 
        vicars: [...(prev.vicars || []), newVicar] 
      }));
    }
  };

  const removeVicar = (index) => {
    if (editingId) {
      const newVicars = editingData.vicars.filter((_, i) => i !== index);
      setEditingData(prev => ({ ...prev, vicars: newVicars }));
    } else {
      const newVicars = newChurchData.vicars.filter((_, i) => i !== index);
      setNewChurchData(prev => ({ ...prev, vicars: newVicars }));
    }
  };

  const fetchChurches = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/churches`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch churches');
      }
      const data = await response.json();
      setChurches(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChurches();
  }, []);

  if (loading) {
    return <div className="loading">Loading Churches...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="committee-members-container">
      {SECTION_ORDER.map(sectionKey => (
        <div key={sectionKey} className="committee-section">
          <h3>{SECTIONS[sectionKey]}</h3>
          <table className="committee-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>Maps</th>
                <th>Vicars</th>
                <th>Priority</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {churches[sectionKey]?.map(church => {
                const isEditing = editingId === church.id;
                const isExpanded = expandedVicars.has(church.id);
                return (
                  <React.Fragment key={church.id}>
                    <tr>
                      <td>
                        {isEditing ? (
                          <input 
                            type="text" 
                            name="name" 
                            value={editingData.name} 
                            onChange={handleEditChange} 
                          />
                        ) : (
                          church.name
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <textarea 
                            name="address" 
                            value={editingData.address} 
                            onChange={handleEditChange} 
                          />
                        ) : (
                          church.address
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input 
                            type="text" 
                            name="mapsLocation" 
                            value={editingData.mapsLocation || ''} 
                            onChange={handleEditChange} 
                            placeholder="Maps URL"
                          />
                        ) : (
                          church.mapsLocation ? (
                            <a href={church.mapsLocation} target="_blank" rel="noopener noreferrer">
                              📍 View Map
                            </a>
                          ) : (
                            'N/A'
                          )
                        )}
                      </td>
                      <td>
                        <button 
                          className="action-btn" 
                          onClick={() => toggleVicarsExpanded(church.id)}
                        >
                          {church.vicars?.length || 0} vicars {isExpanded ? '▲' : '▼'}
                        </button>
                      </td>
                      <td>
                        {isEditing ? (
                          <input 
                            type="number" 
                            name="sortPriority" 
                            value={editingData.sortPriority} 
                            onChange={handleEditChange} 
                            min="1" 
                          />
                        ) : (
                          church.sortPriority
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <>
                            <button className="action-btn save" onClick={handleSaveClick}>💾</button>
                            <button className="action-btn cancel" onClick={handleCancelClick}>❌</button>
                          </>
                        ) : (
                          <>
                            <button className="action-btn edit" onClick={() => handleEditClick(church)}>✏️</button>
                            <button className="action-btn delete" onClick={() => handleDeleteClick(church.id)}>🗑️</button>
                          </>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan="6" className="vicars-expanded">
                          <div className="vicars-section">
                            <h4>Vicars</h4>
                            {(isEditing ? editingData.vicars : church.vicars)?.map((vicar, index) => (
                              <div key={index} className="vicar-row">
                                {isEditing ? (
                                  <>
                                    <input 
                                      type="text" 
                                      placeholder="Title" 
                                      value={vicar.title || ''} 
                                      onChange={(e) => handleVicarChange(index, 'title', e.target.value)}
                                    />
                                    <input 
                                      type="text" 
                                      placeholder="Name" 
                                      value={vicar.name || ''} 
                                      onChange={(e) => handleVicarChange(index, 'name', e.target.value)}
                                    />
                                    <input 
                                      type="text" 
                                      placeholder="Mobile" 
                                      value={vicar.mobile || ''} 
                                      onChange={(e) => handleVicarChange(index, 'mobile', e.target.value)}
                                    />
                                    <input 
                                      type="email" 
                                      placeholder="Email" 
                                      value={vicar.email || ''} 
                                      onChange={(e) => handleVicarChange(index, 'email', e.target.value)}
                                    />
                                    <button 
                                      className="remove-btn" 
                                      onClick={() => removeVicar(index)}
                                    >
                                      ➖
                                    </button>
                                  </>
                                ) : (
                                  <div className="vicar-info">
                                    <strong>{vicar.title}</strong> {vicar.name}
                                    {vicar.mobile && <span> | 📱 {vicar.mobile}</span>}
                                    {vicar.email && <span> | ✉️ {vicar.email}</span>}
                                  </div>
                                )}
                              </div>
                            ))}
                            {isEditing && (
                              <button className="add-btn" onClick={addVicar}>
                                ➕ Add Vicar
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {addingSection === sectionKey && (
                <tr className="add-new-row">
                  <td>
                    <input 
                      type="text" 
                      name="name" 
                      placeholder="Church Name" 
                      value={newChurchData.name} 
                      onChange={handleNewChurchChange} 
                    />
                  </td>
                  <td>
                    <textarea 
                      name="address" 
                      placeholder="Address" 
                      value={newChurchData.address} 
                      onChange={handleNewChurchChange} 
                    />
                  </td>
                  <td>
                    <input 
                      type="text" 
                      name="mapsLocation" 
                      placeholder="Maps URL" 
                      value={newChurchData.mapsLocation} 
                      onChange={handleNewChurchChange} 
                    />
                  </td>
                  <td>
                    <button className="add-btn" onClick={addVicar}>
                      ➕ Add Vicar
                    </button>
                  </td>
                  <td>
                    <input 
                      type="number" 
                      name="sortPriority" 
                      placeholder="Priority" 
                      value={newChurchData.sortPriority} 
                      onChange={handleNewChurchChange} 
                      min="1" 
                    />
                  </td>
                  <td>
                    <button className="action-btn save" onClick={handleAddNewChurch}>💾</button>
                    <button className="action-btn cancel" onClick={handleCancelAdd}>❌</button>
                  </td>
                </tr>
              )}
              {addingSection === sectionKey && newChurchData.vicars.length > 0 && (
                <tr>
                  <td colSpan="6" className="vicars-expanded">
                    <div className="vicars-section">
                      <h4>New Church Vicars</h4>
                      {newChurchData.vicars.map((vicar, index) => (
                        <div key={index} className="vicar-row">
                          <input 
                            type="text" 
                            placeholder="Title" 
                            value={vicar.title || ''} 
                            onChange={(e) => handleVicarChange(index, 'title', e.target.value)}
                          />
                          <input 
                            type="text" 
                            placeholder="Name" 
                            value={vicar.name || ''} 
                            onChange={(e) => handleVicarChange(index, 'name', e.target.value)}
                          />
                          <input 
                            type="text" 
                            placeholder="Mobile" 
                            value={vicar.mobile || ''} 
                            onChange={(e) => handleVicarChange(index, 'mobile', e.target.value)}
                          />
                          <input 
                            type="email" 
                            placeholder="Email" 
                            value={vicar.email || ''} 
                            onChange={(e) => handleVicarChange(index, 'email', e.target.value)}
                          />
                          <button 
                            className="remove-btn" 
                            onClick={() => removeVicar(index)}
                          >
                            ➖
                          </button>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="section-footer">
            <button className="add-btn" onClick={() => handleAddClick(sectionKey)}>
              + Add Church
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ChurchesAroundUs;